import {
  SegmentedControl,
  SegmentedControlItem,
} from '@synoptic/ui-kit/segmented-control/segmented-control.js';
import { Separator } from '@synoptic/ui-kit/separator/separator.js';
import { AnimatePresence, motion } from 'motion/react';
import { useId, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { NodeDataMini } from '@/services/twttr-graph-service';
import { communityLabels } from './graph-community-labels';
import { SigmaShareButton } from './sigma-share-panel.client';
import {
  inlineOptionGroup,
  optionLabel,
  optionStack,
  optionText,
  panelSeparator,
  panelTitle,
} from '../route.css';

export const SigmaOptionsSection = ({
  nodes,
  preview,
}: {
  nodes: NodeDataMini[];
  preview?: boolean;
}) => {
  const id = useId();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedNode = searchParams.get('node');

  const selectedNodeData = useMemo(
    () =>
      selectedNode
        ? nodes.find((node) => node.id === Number(selectedNode))
        : null,
    [nodes, selectedNode],
  );

  const edgeDirection = searchParams.get('dir') === 'out' ? 'out' : 'in';

  if (preview && !selectedNodeData) {
    return null;
  }

  return (
    <div className={optionStack}>
      <AnimatePresence initial={false}>
        {selectedNodeData ? (
          <motion.div
            style={{ overflow: 'hidden' }}
            initial={{
              opacity: 0,
              height: 0,
              transform: 'translateY(12px)',
              filter: 'blur(8px)',
            }}
            animate={{
              opacity: 1,
              height: 'auto',
              transform: 'translateY(0)',
              filter: 'blur(0)',
            }}
            exit={{
              opacity: 0,
              height: 0,
              transform: 'translateY(12px)',
              filter: 'blur(8px)',
            }}
          >
            {preview ? null : (
              <div>
                <h3 className={panelTitle}>Graph Options</h3>

                <div>
                  <div className={inlineOptionGroup}>
                    <label htmlFor={id} className={optionLabel}>
                      Edge direction:
                    </label>

                    <SegmentedControl
                      id={id}
                      value={edgeDirection}
                      onValueChange={(value) => {
                        setSearchParams((prev) => {
                          prev.set('dir', value);
                          return prev;
                        });
                      }}
                    >
                      <SegmentedControlItem value="in">
                        Followers
                      </SegmentedControlItem>
                      <SegmentedControlItem value="out">
                        Following
                      </SegmentedControlItem>
                    </SegmentedControl>
                  </div>
                </div>
              </div>
            )}

            {!preview ? <Separator className={panelSeparator} /> : null}

            <h3 className={panelTitle}>Selected Node:</h3>

            <div className={inlineOptionGroup}>
              <span className={optionLabel}>Username:</span>
              <span className={optionText}>
                <a
                  target={'_blank'}
                  href={`https://x.com/${selectedNodeData.screen_name}`}
                  rel="noreferrer"
                >
                  @{selectedNodeData.screen_name}
                </a>
              </span>
            </div>

            <div className={inlineOptionGroup}>
              <span className={optionLabel}>Name:</span>
              <span className={optionText}>{selectedNodeData.name}</span>
            </div>

            <div className={inlineOptionGroup}>
              <span className={optionLabel}>Community:</span>
              <span className={optionText}>
                {communityLabels[selectedNodeData.community].community_label}
              </span>
            </div>

            {!preview ? <Separator className={panelSeparator} /> : null}
          </motion.div>
        ) : null}
      </AnimatePresence>

      {preview ? null : (
        <>
          <SigmaShareButton selectedNodeData={selectedNodeData} />
        </>
      )}
    </div>
  );
};
